// Tenants: search, status filter, sort, paging. Everything lives in the URL
// (?q=&status=&sort=&page=), so a filtered view can be shared, reloaded,
// and survives Back from a tenant page.

import { useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import { api } from '@/api'
import { DEFAULT_PAGE_SIZE } from '@/api/tenants'
import { ACCOUNT_STATUSES, ADDON_KEYS, TENANT_SOURCES, type AccountStatus, type AddonKey, type TenantSort, type TenantSource, type TenantSummary } from '@/api/types'
import { PageHeader } from '@/components/shell/PageHeader'
import {
  AccountStatusPill,
  Button,
  ButtonLink,
  Card,
  DataTable,
  EmptyState,
  ErrorState,
  FilterChips,
  Pagination,
  PlanPill,
  PlusIcon,
  SourcePill,
  RelativeTime,
  SearchInput,
  SelectInput,
  SkeletonRows,
  type Column,
} from '@/components/ui'
import { accountStatusLabel } from '@/lib/status'
import { ADDONS, enabledAddons } from '@/lib/addons'
import { cn, formatDate, formatRelativeDays, formatShortDate, titleCase } from '@/lib/format'
import { useDocumentTitle } from '@/lib/hooks'
import { useQuery } from '@/lib/query'
import { PLATFORM_DOMAIN } from '@/lib/tenant'

type StatusFilter = AccountStatus | 'all'

const SORTS: Array<{ value: TenantSort; label: string }> = [
  { value: 'name', label: 'Name A–Z' },
  { value: '-created_at', label: 'Newest first' },
  { value: 'created_at', label: 'Oldest first' },
  { value: 'renewal', label: 'Renewal soonest' },
  { value: '-last_activity_at', label: 'Recently active' },
]

// #1387: where the league came from. "Self-serve trials" is source + the Trial chip.
const SOURCE_FILTERS = [
  { value: '', label: 'Any source' },
  { value: 'self_serve', label: 'Self-serve' },
  { value: 'portal', label: 'Made in the portal' },
]
const SOURCE_LABEL: Record<TenantSource, string> = { self_serve: 'self-serve', portal: 'made in the portal' }

const ADDON_FILTERS = [{ value: '', label: 'Any add-on' }, ...ADDON_KEYS.map((k) => ({ value: k, label: ADDONS[k].label }))]

const STATUS_CHIP_LABEL: Record<StatusFilter, string> = {
  all: 'All',
  trialing: 'Trial',
  active: accountStatusLabel('active'),
  past_due: accountStatusLabel('past_due'),
  suspended: accountStatusLabel('suspended'),
  canceled: accountStatusLabel('canceled'),
}

function readParams(params: URLSearchParams) {
  const status = params.get('status')
  const sort = params.get('sort')
  const page = Number(params.get('page'))
  const addon = params.get('addon')
  const source = params.get('source')
  return {
    source: (TENANT_SOURCES as readonly string[]).includes(source ?? '') ? (source as TenantSource) : undefined,
    q: params.get('q') ?? '',
    addon: (ADDON_KEYS as readonly string[]).includes(addon ?? '') ? (addon as AddonKey) : undefined,
    status: (ACCOUNT_STATUSES as readonly string[]).includes(status ?? '') ? (status as AccountStatus) : undefined,
    sort: SORTS.some((s) => s.value === sort) ? (sort as TenantSort) : ('name' as TenantSort),
    page: Number.isInteger(page) && page > 0 ? page : 1,
  }
}

export default function TenantsListPage() {
  useDocumentTitle('Tenants')
  const [searchParams, setSearchParams] = useSearchParams()
  const { q, status, sort, page, addon, source } = readParams(searchParams)

  const update = (patch: Record<string, string | number | undefined>, resetPage = true) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev)
        for (const [k, v] of Object.entries(patch)) {
          if (v === undefined || v === '' || (k === 'sort' && v === 'name') || (k === 'page' && v === 1)) next.delete(k)
          else next.set(k, String(v))
        }
        if (resetPage && !('page' in patch)) next.delete('page')
        return next
      },
      { replace: true },
    )
  }

  const params = useMemo(() => ({ q, status, sort, page, addon, source, page_size: DEFAULT_PAGE_SIZE }), [q, status, sort, page, addon, source])
  const { data, error, status: loadStatus, isFetching, refetch } = useQuery(
    ['tenants', 'list', params],
    (signal) => api.tenants.list(params, signal),
    { keepPrevious: true },
  )

  const counts = data?.status_counts
  const statusOptions = (['all', ...ACCOUNT_STATUSES] as StatusFilter[]).map((s) => ({
    value: s,
    label: STATUS_CHIP_LABEL[s],
    count: counts ? counts[s] : undefined,
  }))

  const hasFilters = !!q || !!status || !!addon || !!source
  const selfServeTrials = source === 'self_serve' && status === 'trialing'
  const rows = data?.leagues ?? []

  return (
    <div className="grid grid-cols-1 gap-5">
      <PageHeader
        title="Tenants"
        description={!counts ? 'Every league on the platform.' : q ? `${counts.all} ${counts.all === 1 ? 'league matches' : 'leagues match'} “${q}”.` : `${counts.all} ${counts.all === 1 ? 'league' : 'leagues'} on the platform.`}
        actions={
          <>
            {!selfServeTrials ? (
              <ButtonLink to="/tenants?source=self_serve&status=trialing">
                Self-serve trials
              </ButtonLink>
            ) : null}
            <ButtonLink to="/tenants/new" variant="primary" icon={<PlusIcon size={16} />}>
              New tenant
            </ButtonLink>
          </>
        }
      />

      <div className="grid gap-3">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
          <SearchInput
            label="Search tenants"
            placeholder="Search name, subdomain or domain"
            value={q}
            onChange={(v) => update({ q: v })}
            className="sm:max-w-sm sm:flex-1"
          />
          <label className="flex items-center gap-2 sm:ml-auto">
            <span className="w-10 shrink-0 text-xs font-semibold text-muted sm:w-auto">From</span>
            <SelectInput
              aria-label="Filter by source"
              value={source ?? ''}
              options={SOURCE_FILTERS}
              onChange={(e) => update({ source: e.target.value || undefined })}
              className="h-8 sm:w-44"
            />
          </label>
          <label className="flex items-center gap-2">
            <span className="w-10 shrink-0 text-xs font-semibold text-muted sm:w-auto">Has</span>
            <SelectInput
              aria-label="Filter by add-on"
              value={addon ?? ''}
              options={ADDON_FILTERS}
              onChange={(e) => update({ addon: e.target.value || undefined })}
              className="h-8 sm:w-40"
            />
          </label>
          <label className="flex items-center gap-2">
            <span className="w-10 shrink-0 text-xs font-semibold text-muted sm:w-auto">Sort</span>
            <SelectInput
              aria-label="Sort tenants"
              value={sort}
              options={SORTS}
              onChange={(e) => update({ sort: e.target.value })}
              className="h-8 sm:w-44"
            />
          </label>
        </div>
        <FilterChips label="Filter by status" options={statusOptions} value={status ?? 'all'} onChange={(v) => update({ status: v === 'all' ? undefined : v })} />
      </div>

      <Card flush className="overflow-hidden">
        {loadStatus === 'loading' ? (
          <SkeletonRows rows={6} label="Loading tenants" />
        ) : loadStatus === 'error' && !data ? (
          <ErrorState error={error} title="Couldn't load tenants" onRetry={refetch} />
        ) : rows.length === 0 ? (
          // ?page= past the end of a non-empty list (an old link, or the list shrank).
          data && data.total > 0 ? (
            <EmptyState
              title="No results on this page"
              description={`Page ${page} is past the end. ${data.total} ${data.total === 1 ? 'tenant' : 'tenants'}${hasFilters ? ' match' : ' in all'}.`}
              action={
                <Button size="sm" onClick={() => update({ page: 1 }, false)}>
                  Go to page 1
                </Button>
              }
            />
          ) : hasFilters ? (
            <EmptyState
              title="No tenants match"
              description={`Nothing matches${q ? ` “${q}”` : ''}${status ? ` with status ${STATUS_CHIP_LABEL[status]}` : ''}${addon ? ` with ${ADDONS[addon].label} on` : ''}${source ? `, ${SOURCE_LABEL[source]}` : ''}.`}
              action={
                <Button size="sm" onClick={() => update({ q: undefined, status: undefined, addon: undefined, source: undefined })}>
                  Clear filters
                </Button>
              }
            />
          ) : (
            <EmptyState
              title="No tenants yet"
              description="Create the first league to get started."
              action={
                <ButtonLink to="/tenants/new" variant="primary" size="sm" icon={<PlusIcon size={14} />}>
                  New tenant
                </ButtonLink>
              }
            />
          )
        ) : (
          <>
            {loadStatus === 'error' ? (
              <div role="alert" className="flex flex-wrap items-center justify-between gap-2 border-b border-line bg-bad-soft/40 px-4 py-2 text-xs text-bad">
                Couldn't refresh. Showing the last results.
                <Button size="sm" variant="ghost" onClick={refetch}>
                  Retry
                </Button>
              </div>
            ) : null}
            <DataTable
              caption="Tenants"
              rows={rows}
              columns={COLUMNS}
              rowKey={(t) => t.id}
              rowHref={(t) => `/tenants/${t.id}/overview`}
              renderCard={(t) => <TenantCard tenant={t} />}
              busy={isFetching && loadStatus === 'success'}
            />
            <div className="border-t border-line px-4 py-3">
              <Pagination page={data?.page ?? page} pageSize={data?.page_size ?? DEFAULT_PAGE_SIZE} total={data?.total ?? 0} noun="tenants" onPageChange={(p) => update({ page: p }, false)} />
            </div>
          </>
        )}
      </Card>
    </div>
  )
}

// ─── Cells ──────────────────────────────────────────────────────────────────

function TenantName({ tenant }: { tenant: TenantSummary }) {
  return (
    <span className="grid min-w-0 gap-0.5">
      <span className="flex flex-wrap items-center gap-1.5">
        <span className="break-words font-semibold">{tenant.name}</span>
        <SourcePill source={tenant.source} />
      </span>
      <span className="truncate font-mono text-xs font-medium text-muted">{tenant.subdomain ? `${tenant.subdomain}.${PLATFORM_DOMAIN}` : 'no subdomain'}</span>
    </span>
  )
}

/** Six segments, one per add-on, lit when on. */
function AddonMeter({ tenant }: { tenant: TenantSummary }) {
  const on = enabledAddons(tenant.addons)
  const label = on.length ? on.map((k) => ADDONS[k].label).join(', ') : 'None'
  return (
    <span className="inline-flex items-center gap-2" title={label}>
      <span aria-hidden className="flex gap-0.5">
        {ADDON_KEYS.map((k) => (
          <span key={k} className={cn('h-3 w-1.5 rounded-[1px]', tenant.addons[k] ? 'bg-accent' : 'bg-toggle-off')} />
        ))}
      </span>
      <span className="font-mono text-xs text-muted tabular">
        <span className="sr-only">Add-ons: {label}. </span>
        {on.length}/{ADDON_KEYS.length}
      </span>
    </span>
  )
}

function DomainCell({ tenant }: { tenant: TenantSummary }) {
  if (!tenant.custom_domain) return <span className="text-subtle">—</span>
  const verified = !!tenant.custom_domain_verified_at
  return (
    <span className="inline-flex min-w-0 items-center gap-1.5" title={verified ? 'Verified' : 'Waiting on DNS'}>
      <span aria-hidden className={cn('h-1.5 w-1.5 shrink-0 rounded-full', verified ? 'bg-good' : 'bg-warn')} />
      <span className="truncate font-mono text-xs">{tenant.custom_domain}</span>
      <span className="sr-only">{verified ? '(verified)' : '(pending)'}</span>
    </span>
  )
}

function RenewalCell({ tenant }: { tenant: TenantSummary }) {
  const a = tenant.account
  const iso = a.status === 'trialing' ? a.trial_ends_at : a.status === 'active' || a.status === 'past_due' ? a.current_period_end : null
  if (!iso) return <span className="text-subtle">—</span>
  const label = a.status === 'trialing' ? 'Trial ends' : 'Renews'
  return (
    <span className="grid gap-0.5 tabular">
      <span>
        <span className="sr-only">{label} </span>
        {formatShortDate(iso)}
      </span>
      <span className="text-xs text-muted">{formatRelativeDays(iso)}</span>
    </span>
  )
}

function RenewalLine({ tenant }: { tenant: TenantSummary }) {
  const a = tenant.account
  const iso = a.status === 'trialing' ? a.trial_ends_at : a.status === 'active' || a.status === 'past_due' ? a.current_period_end : null
  if (!iso) return null
  return (
    <span className="text-xs text-muted tabular">
      {a.status === 'trialing' ? 'Trial ends' : 'Renews'} {formatShortDate(iso)} · {formatRelativeDays(iso)}
    </span>
  )
}

const COLUMNS: Column<TenantSummary>[] = [
  { id: 'name', header: 'Tenant', primary: true, cell: (t) => <TenantName tenant={t} />, className: 'min-w-[13rem] max-w-[18rem]' },
  { id: 'status', header: 'Status', cell: (t) => <AccountStatusPill account={t.account} /> },
  { id: 'plan', header: 'Plan', cell: (t) => <PlanPill account={t.account} /> },
  { id: 'addons', header: 'Add-ons', cell: (t) => <AddonMeter tenant={t} /> },
  { id: 'domain', header: 'Custom domain', cell: (t) => <DomainCell tenant={t} />, className: 'max-w-[14rem]' },
  { id: 'renewal', header: 'Renews / trial', cell: (t) => <RenewalCell tenant={t} /> },
  { id: 'sport', header: 'Sport', cell: (t) => <span className="text-muted">{titleCase(t.default_sport_id)}</span> },
  {
    id: 'activity',
    header: 'Last active',
    cell: (t) => (t.last_activity_at ? <RelativeTime value={t.last_activity_at} staleAfterDays={14} className="text-muted" /> : <span className="text-subtle">—</span>),
  },
  { id: 'created', header: 'Created', cell: (t) => <span className="whitespace-nowrap text-muted tabular">{formatDate(t.created_at)}</span> },
]

function TenantCard({ tenant }: { tenant: TenantSummary }) {
  return (
    <div className="grid gap-2">
      <TenantName tenant={tenant} />
      <div className="flex flex-wrap items-center gap-1.5">
        <AccountStatusPill account={tenant.account} />
        <PlanPill account={tenant.account} />
        <AddonMeter tenant={tenant} />
      </div>
      {tenant.custom_domain ? <DomainCell tenant={tenant} /> : null}
      <RenewalLine tenant={tenant} />
    </div>
  )
}
